import { type ReactNode } from 'react';

import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';

import { ItemLink } from '@/components/item/itemLink';
import { ItemType } from '@/components/item/itemType';
import { SandboxLink } from '@/components/sandboxLink';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DisplayName } from '@/components/ui/display-name';
import { Panel } from '@/components/ui/panel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Text } from '@/components/ui/typography';
import { PagePermissions } from '@/models/pagePermissions';

import {
  publishOutcome,
  type PublishOutcome,
  publishSections,
  type PublishResponse,
  type PublishResultBase,
  type PublishSandboxItemStatus,
  type PublishSandboxResponse,
} from '../models/publishSandbox';

const renderStatusBadge = (itemStatus: PublishSandboxItemStatus) => {
  switch (itemStatus) {
    case 'published':
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200"
        >
          <CheckCircle className="h-3 w-3 mr-1" />
          Published
        </Badge>
      );
    case 'failed':
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-700 border-red-200"
        >
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    case 'skipped':
      return (
        <Badge
          variant="outline"
          className="bg-gray-50 text-gray-700 border-gray-200"
        >
          <AlertCircle className="h-3 w-3 mr-1" />
          Skipped
        </Badge>
      );
  }
};

interface BasePublishResult {
  status: PublishSandboxItemStatus;
  error: string | null;
}

interface PublishResultsTableProps<T extends BasePublishResult> {
  title: string;
  data: T[];
  keyExtractor: (item: T) => string;
  renderName: (item: T) => ReactNode;
}

const PublishResultsTable = <T extends BasePublishResult>({
  title,
  data,
  keyExtractor,
  renderName,
}: PublishResultsTableProps<T>) => {
  if (data.length === 0) return null;

  return (
    <Panel title={title} count={data.length}>
      <Table noWrapper>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Status</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Error</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item) => (
            <TableRow key={keyExtractor(item)}>
              <TableCell>{renderStatusBadge(item.status)}</TableCell>
              <TableCell className="font-medium">{renderName(item)}</TableCell>
              <TableCell>
                <div className="max-w-[45ch] overflow-hidden">
                  {item.error ? (
                    <span className="inline-block text-xs text-red-600 font-mono bg-red-50 px-2 py-1 rounded whitespace-break-spaces">
                      {item.error}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
};
const OUTCOME_STYLE = {
  complete: {
    icon: CheckCircle,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-900',
    title: 'Publish completed successfully',
    message: 'Everything went well.',
  },
  partial: {
    icon: AlertCircle,
    iconColor: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    textColor: 'text-yellow-900',
    title: 'Publish partially completed',
    message: 'Some resources were not published. Check the logs for details.',
  },
  failed: {
    icon: XCircle,
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-900',
    title: 'Publish failed',
    message: 'Check the logs for details.',
  },
} satisfies Record<PublishOutcome, unknown>;

const PublishStatCard = ({
  title,
  counts,
}: {
  title: string;
  counts: Pick<
    PublishResponse<PublishResultBase>,
    'total' | 'published' | 'failed'
  >;
}) => (
  <Card className="flex-1">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-lg font-bold text-muted-foreground">
            {counts.total}
          </div>
          <div className="text-xs text-muted-foreground">Total</div>
        </div>
        <div>
          <div
            className={`text-lg font-bold ${counts.published > 0 ? 'text-green-600' : 'text-muted-foreground'}`}
          >
            {counts.published}
          </div>
          <div className="text-xs text-muted-foreground">Published</div>
        </div>
        <div>
          <div
            className={`text-lg font-bold ${counts.failed > 0 ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {counts.failed}
          </div>
          <div className="text-xs text-muted-foreground">Failed</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

interface SandboxPublishSummaryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  response: PublishSandboxResponse;
}

export const SandboxPublishSummaryDialog = ({
  open,
  onOpenChange,
  response,
}: SandboxPublishSummaryDialogProps) => {
  const statusConfig = OUTCOME_STYLE[publishOutcome(response)];
  const StatusIcon = statusConfig.icon;
  // A followed link lands on a page this modal would otherwise keep covering.
  const closeOnFollow = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publish Summary</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <Card
            className={`${statusConfig.borderColor} ${statusConfig.bgColor}`}
          >
            <CardContent>
              <div className="flex flex-row items-start gap-4">
                <div className={`${statusConfig.iconColor} mt-1`}>
                  <StatusIcon className="size-6" />
                </div>
                <div>
                  <Text
                    variant="heading"
                    as="h3"
                    className={statusConfig.textColor}
                  >
                    {statusConfig.title}
                  </Text>
                  <p
                    className={`text-sm mt-1 ${statusConfig.textColor} opacity-80`}
                  >
                    {statusConfig.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-4 justify-between">
            {publishSections(response)
              .filter(([, counts]) => counts.total > 0)
              .map(([title, counts]) => (
                <PublishStatCard key={title} title={title} counts={counts} />
              ))}
          </div>

          <PublishResultsTable
            title="Catalog Elements Details"
            data={response.items.results}
            keyExtractor={(item) => item.item_id}
            renderName={(item) => (
              <div className="flex items-center gap-2">
                <ItemType type={item.type} size={16} />
                <ItemLink
                  itemId={item.item_id}
                  type={item.type}
                  className="hover:underline"
                  onClick={closeOnFollow}
                >
                  <DisplayName name={item.name} maxWidth="200px" />
                </ItemLink>
              </div>
            )}
          />

          <PublishResultsTable
            title="Snapshots Details"
            data={response.snapshots.results}
            keyExtractor={(snapshot) => snapshot.snapshot_id}
            renderName={(snapshot) => (
              <SandboxLink
                to={`store/snapshot/${snapshot.snapshot_id}`}
                oneOf={PagePermissions.snapshots}
                className="hover:underline"
                onClick={closeOnFollow}
              >
                <DisplayName name={snapshot.name} maxWidth="250px" />
              </SandboxLink>
            )}
          />

          <PublishResultsTable
            title="Gifts Details"
            data={response.gifts.results}
            keyExtractor={(gift) => gift.gift_id}
            renderName={(gift) => (
              <div className="flex flex-col">
                <span>{gift.code}</span>
                <DisplayName
                  name={gift.name}
                  maxWidth="200px"
                  className="text-xs text-muted-foreground"
                />
              </div>
            )}
          />

          {/* Season Passes Table (failures only) */}
          <PublishResultsTable
            title="Season Passes Details (failures)"
            data={response.season_passes.results}
            keyExtractor={(seasonPass) => seasonPass.season_pass_id}
            renderName={(seasonPass) => (
              <div className="flex flex-col">
                <SandboxLink
                  to={`season-passes?show=${seasonPass.season_pass_id}`}
                  oneOf={PagePermissions.seasonPasses}
                  className="hover:underline"
                  onClick={closeOnFollow}
                >
                  {seasonPass.code}
                </SandboxLink>
                <DisplayName
                  name={seasonPass.name}
                  maxWidth="200px"
                  className="text-xs text-muted-foreground"
                />
              </div>
            )}
          />
        </div>
        <DialogFooter>
          <Button
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
